<template>
    <!-- Forms Container -->
    <div class="forms-container" v-if="!isLoading && safeForms.length && safeFormConfig.length">
        <div v-for="(form, fIdx) in safeFormConfig" :key="'form-' + form._mainId" class="form-wrapper"
            :class="{ prev: currentFormIndex > fIdx, active: currentFormIndex === fIdx, next: currentFormIndex < fIdx }">
            <div class="form-card mt-0">
                <template v-if="form.sections && form.sections.length">
                    <template v-for="(section, sIdx) in form.sections"
                        :key="'section-' + form._mainId + '-' + section.id">
                        <!-- Only show section if it has access and action_name is not NO_ACCESS -->
                        <div v-if="hasSectionAccess(section)" class="mb-1">
                            <div class="card border border-dark border-1 rounded-lg mb-2 p-3 shadow">
                                <!-- Section header -->
                                <div class="section-header text-x fw-bold d-flex justify-content-between align-items-center border-bottom border-2 pb-3"
                                     v-if="section.title" @click="toggle_section(fIdx, sIdx)">

                                    <!-- Left side -->
                                    <div class="d-flex align-items-center gap-2 flex-shrink-0">
                                        <span class="text-capitalize">{{ section.title }}</span>
                                        <span :class="section.is_verified ? 'text-success' : 'text-danger'" class="fs-5">
                                            <i :class="section.is_verified ? 'bi bi-check-circle' : 'bi bi-exclamation-circle'"></i>
                                        </span>
                                    </div>

                                    <!-- Right side: Badge above, content below -->
                                    <div class="d-flex flex-column align-items-end">
                                        <!-- Badge always on top -->
                                        <span class="status-badge text-xs"
                                            :class="section.is_verified ? 'manually-verified' : 'pending'">
                                            {{ section.is_verified ? 'Manually Verified' : 'Pending Verification' }}
                                        </span>

                                        <!-- Everything else in one line -->
                                        <div class="d-flex align-items-center gap-2 mt-1">
                                            <div v-if="userRole === 'System Admin' && hasWriteAccess(section)" @click.stop>
                                                <div v-if="!section.is_verified" class="form-check mb-0">
                                                    <input class="form-check-input shadow-sm" type="checkbox"
                                                        v-model="section.is_verified" :id="`section-${section.id}-manual`">
                                                    <label class="form-check-label text-xxs mb-0">Manual Verify</label>
                                                </div>
                                            </div>
                                            <i :class="is_section_open(fIdx, sIdx) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
                                        </div>
                                    </div>
                                </div>

                                <!-- Fields -->
                                <div class="mt-1"
                                    v-if="section.fields && section.fields.length && is_section_open(fIdx, sIdx) && safeForms[fIdx]">
                                    <template v-for="(field, fieldIndex) in section.fields"
                                        :key="'field-' + form._mainId + '-' + section.id + '-' + field.id">

                                        <!-- Main Question Container -->
                                        <div class="d-flex justify-content-between align-items-start mt-1">
                                            <div class="d-flex flex-grow-1">
                                                <span class="text-xs fw-7 me-1">{{fieldIndex + 1 }}.</span>
                                                    <label class="flex-grow-1 text-dark text-xs text-capitalize fw-7">{{
                                                        field.label }}</label>
                                                    <div v-if="['single_select', 'multi_select', 'radio', 'checkboxes'].includes(field.type)"
                                                        class="mb-1">
                                                    </div>
                                            </div>
                                        </div>

                                        <!-- Main Question Answer -->
                                        <div class="mb-3 ms-2">
                                            <!-- text -->
                                            <input v-if="field.type === 'text'" type="text"
                                                class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]"
                                                :placeholder="'No Answer'"
                                                :disabled="!hasWriteAccess(section)" />

                                            <!-- textarea -->
                                            <textarea v-if="field.type === 'textarea'"
                                                class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]"
                                                :placeholder="'No Answer'"
                                                :disabled="!hasWriteAccess(section)"></textarea>

                                            <!-- number -->
                                            <input v-if="field.type === 'number'" type="number"
                                                class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg"
                                                v-model.number="safeForms[fIdx][field.model]"
                                                :disabled="!hasWriteAccess(section)" />

                                            <!-- date -->
                                            <input v-if="field.type === 'date'" type="date"
                                                class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]"
                                                :disabled="!hasWriteAccess(section)" />

                                            <!-- single select -->
                                            <div v-if="field.type === 'single_select' || field.type === 'radio'"
                                                class="radio-group">
                                                <div v-for="(option, optIndex) in field.options || []"
                                                    :key="`radio-${field.model}-${optIndex}`"
                                                    class="form-check">
                                                    <input class="form-check-input" type="radio"
                                                        :name="`r-${field.model}`"
                                                        :id="`${field.id}-${optIndex}`"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :value="String(option.value)"
                                                        @change="update_dependent_questions_visibility(field, safeForms[fIdx][field.model])"
                                                        :disabled="!hasWriteAccess(section)" />
                                                    <label class="form-check-label text-xxs"
                                                        :for="`${field.id}-${optIndex}`">
                                                        {{ option.text }}
                                                    </label>
                                                </div>
                                                <div v-if="!field.options?.length"
                                                    class="text-muted text-xxs">No
                                                    options</div>
                                            </div>

                                            <!-- multi select -->
                                            <div
                                                v-if="field.type === 'multi_select' || field.type === 'checkboxes'">
                                                <div v-for="(option, optIndex) in field.options || []"
                                                    :key="`chk-${field.model}-${optIndex}`"
                                                    class="form-check">
                                                    <input class="form-check-input" type="checkbox"
                                                        :id="`${field.id}-${optIndex}`"
                                                        :value="String(option.value)"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :disabled="!hasWriteAccess(section)" />
                                                    <label class="form-check-label text-xxs"
                                                        :for="`${field.id}-${optIndex}`">
                                                        {{ option.text }}
                                                    </label>
                                                </div>
                                            </div>

                                            <!-- file -->
                                            <div v-else-if="field.type === 'file'">
                                                <template v-if="file_exitsts(fIdx, field)">
                                                    <div class="d-flex align-items-center gap-2 flex-wrap">
                                                        <span class="text-xs">{{ (fIdx, field).file_name }}</span>
                                                        <button
                                                            class="btn btn-primary-dark btn-sm rounded-small text-xxs text-dark"
                                                            type="button"
                                                            @click="open_file_preview(fIdx, field)">
                                                            Preview
                                                        </button>
                                                        <button
                                                            class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-white"
                                                            type="button"
                                                            @click="remove_file(fIdx, field.model)">
                                                            Remove
                                                        </button>
                                                    </div>
                                                </template>

                                                <template v-else>
                                                    <input v-if="hasWriteAccess(section)"
                                                        type="file"
                                                        class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg"
                                                        :id="field.id || ''"
                                                        @change="handle_file_upload($event, field, fIdx)"
                                                        :required="field.required" />
                                                    <div v-else class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg">
                                                        No file uploaded
                                                    </div>
                                                </template>
                                            </div>

                                            <!-- facial verification -->
                                            <div v-else-if="field.type === 'facial_verification'">
                                                <template v-if="file_exitsts(fIdx, field)">
                                                    <div
                                                        class="d-flex align-items-center gap-2 flex-wrap">
                                                        <span class="text-xs">{{ file_name(fIdx, field)
                                                            }}</span>
                                                        <button
                                                            class="btn btn-primary-dark btn-sm rounded-small text-xxs text-dark"
                                                            type="button"
                                                            @click="open_file_preview(fIdx, field)">
                                                            Preview
                                                        </button>
                                                    </div>
                                                </template>

                                                <div v-else-if="hasWriteAccess(section)"
                                                    class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg">
                                                    Facial verification component would appear here
                                                </div>
                                                <div v-else class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg">
                                                    No file uploaded - Upload disabled for read-only
                                                    access
                                                </div>
                                            </div>

                                            <!-- fallback -->
                                            <input
                                                v-if="['text','textarea','number','date','single_select','multi_select','checkboxes','file','facial_verification'].indexOf(field.type) === -1"
                                                type="text"
                                                class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg"
                                                :id="field.id || ''"
                                                v-model="safeForms[fIdx][field.model]"
                                                :placeholder="field.placeholder || 'No Answer'"
                                                :required="field.required"
                                                :disabled="!hasWriteAccess(section)" />
                                        </div>

                                        <!-- Dependent Questions Section -->
                                        <div
                                        v-if="field.dependent_questions && field.dependent_questions.length"
                                        class="mt-2 ms-4 ps-3 border-start border-3 border-primary"
                                        >
                                        <!-- Group label -->
                                        <div
                                            v-for="(dq, dq_index) in field.dependent_questions"
                                            :key="'dq-' + dq_index"
                                            v-show="dq.is_visible"
                                            class=" rounded p-3 mb-3 shadow-sm"
                                        >


                                            <!-- Dependent question label -->
                                            <div class="d-flex align-items-start mb-1">
                                            <span class="text-xs fw-7 me-1">
                                                {{ fieldIndex + 1 }}.{{ dq_index + 1 }}
                                            </span>
                                            <label class="text-dark fw-7 text-xs text-capitalize">
                                                {{ dq.label }}
                                                <span v-if="dq.required" class="text-danger">*</span>
                                            </label>
                                            </div>

                                            <!-- Dependent Question Answer -->
                                            <div class="mt-1">
                                            <input
                                                v-if="dq.type === 'text'"
                                                type="text"
                                                class="form-control form-control-sm text-xxs bg-light border-1 py-1 rounded-lg"
                                                :id="dq.model"
                                                v-model="safeForms[fIdx][dq.model]"
                                                :placeholder="dq.label"
                                                :required="dq.required && dq.is_visible"
                                                :disabled="!hasWriteAccess(section)"
                                            />

                                            <textarea
                                                v-else-if="dq.type === 'textarea'"
                                                class="form-control form-control-sm text-xxs bg-light border-1 py-1 rounded-lg"
                                                :id="dq.model"
                                                v-model="safeForms[fIdx][dq.model]"
                                                :placeholder="dq.label"
                                                rows="3"
                                                :required="dq.required && dq.is_visible"
                                                :disabled="!hasWriteAccess(section)"
                                            ></textarea>

                                            <input
                                                v-else-if="dq.type === 'number'"
                                                type="number"
                                                class="form-control form-control-sm text-xxs bg-light border-1 py-1 rounded-lg"
                                                :id="dq.model"
                                                v-model="safeForms[fIdx][dq.model]"
                                                :required="dq.required && dq.is_visible"
                                                :disabled="!hasWriteAccess(section)"
                                            />

                                            <input
                                                v-else-if="dq.type === 'date'"
                                                type="date"
                                                class="form-control form-control-sm text-xxs bg-light border-1 py-1 rounded-lg"
                                                :id="dq.model"
                                                v-model="safeForms[fIdx][dq.model]"
                                                :required="dq.required && dq.is_visible"
                                                :disabled="!hasWriteAccess(section)"
                                            />

                                            <div v-else-if="dq.type === 'facial_verification'"
                                                class="form-control form-control-sm text-xxs bg-light border-1 py-1 rounded-lg">
                                                <div v-if="hasWriteAccess(section)">
                                                    <!-- FacialBiometric component would go here -->
                                                    <div class="text-muted text-xxs">Facial
                                                        verification interface</div>
                                                </div>
                                                <div v-else class="text-muted text-xxs">
                                                    Facial verification disabled for read-only
                                                    access
                                                </div>
                                            </div>

                                            <div v-else-if="dq.type === 'file'">
                                                <div
                                                    v-if="safeForms[fIdx][dq.model] && safeForms[fIdx][dq.model].file_name">
                                                    <div class="d-flex align-items-center gap-2">
                                                        <span class="text-xs">{{
                                                            safeForms[fIdx][dq.model].file_name
                                                            }}</span>
                                                        <button
                                                            class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                                                            type="button"
                                                            @click="open_file_preview(fIdx, dq)">
                                                            Preview
                                                        </button>
                                                        <button v-if="hasWriteAccess(section)"
                                                            class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-white ms-2"
                                                            type="button"
                                                            @click="remove_file(fIdx, dq.model)">
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>

                                                <div v-else>
                                                    <input v-if="hasWriteAccess(section)"
                                                        type="file"
                                                        class="form-control form-control-sm text-xxs bg-light border-1 py-1 rounded-lg"
                                                        :id="dq.model"
                                                        @change="handle_file_upload($event, dq, fIdx)"
                                                        :required="dq.required && dq.is_visible" />

                                                    <div v-if="!safeForms[fIdx][dq.model]?.file"
                                                        class="mt-2">
                                                        <label class="text-xs"
                                                            :for="dq.model + '_file_comment'">
                                                            Please explain why you cannot upload
                                                            this file:
                                                        </label>
                                                        <textarea :id="dq.model + '_file_comment'"
                                                            class="form-control form-control-sm text-xs bg-light border-1 py-1 rounded-lg"
                                                            v-model="safeForms[fIdx][dq.model + '_file_comment']"
                                                            rows="2"
                                                            placeholder="Provide a reason..."
                                                            :disabled="!hasWriteAccess(section)"></textarea>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Radio -->
                                            <div
                                                v-else-if="dq.type === 'single_select' || dq.type === 'radio'"
                                                class="radio-group mt-2"
                                            >
                                                <div
                                                v-for="(option, opt_index) in dq.options || []"
                                                :key="'dq-radio-' + opt_index"
                                                class="form-check"
                                                >
                                                <input
                                                    class="form-check-input"
                                                    type="radio"
                                                    :name="dq.model"
                                                    :id="`${dq.model}-${opt_index}`"
                                                    v-model="safeForms[fIdx][dq.model]"
                                                    :value="option.value || option.text"
                                                    :required="dq.required && dq.is_visible"
                                                    :disabled="!hasWriteAccess(section)"
                                                />
                                                <label class="form-check-label text-xxs">
                                                    {{ option.text }}
                                                </label>
                                                </div>
                                            </div>

                                            <!-- Checkbox -->
                                            <div
                                                v-else-if="dq.type === 'multi_select' || dq.type === 'checkboxes'"
                                                class="mt-2"
                                            >
                                                <div
                                                v-for="(option, opt_index) in dq.options || []"
                                                :key="'dq-checkbox-' + opt_index"
                                                class="form-check"
                                                >
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    :id="`${dq.model}-${opt_index}`"
                                                    :value="option.value || option.text"
                                                    v-model="safeForms[fIdx][dq.model]"
                                                    :disabled="!hasWriteAccess(section)"
                                                />
                                                <label class="form-check-label text-xxs">
                                                    {{ option.text }}
                                                </label>
                                                </div>
                                            </div>

                                            <!-- File / facial / fallback remain unchanged -->
                                            <slot />
                                            </div>
                                        </div>
                                        </div>
                                    </template>
                                </div>

                                <!-- Verification / Actions - Only show for Write access and System Admin -->
                                <div v-if="userRole === 'System Admin' && hasWriteAccess(section)"
                                    class="d-flex justify-content-end align-items-center mt-3 p-2">
                                    <div class="d-flex gap-2 mt-2">
                                        <button v-if="!section.is_verified"
                                            class="btn btn-sm text-xxs shadow" 
                                            :class="is_verify_allowed ? 'btn-primary-dark' : 'disabled'"
                                            :disabled="!is_verify_allowed"
                                            @click="is_verify_allowed ? section_verification(section.id) : null">
                                            Verify
                                        </button>
                                        <button class="btn btn-sm text-xxs shadow"
                                            :class="is_request_information_allowed ? 'btn-primary-dark' : 'disabled'"
                                            :disabled="!is_request_information_allowed"
                                            data-bs-toggle="modal" data-bs-target="#request_information"
                                            @click="is_request_information_allowed ? prepare_request_information(fIdx, sIdx) : null">
                                            Request Information
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </template>

                <div v-else class="text-muted small">No sections in this tab.</div>
                <div v-if="hasAnyWriteAccess(form)" class="d-flex justify-content-center align-items-center">
                    <button class="btn btn-sm rounded-small text-xxs shadow text-dark"
                    :class="can_submit ? 'btn-primary-dark' : 'disabled'"
                    :disabled="!can_submit"
                    @click="can_submit ? (fIdx === safeFormConfig.length - 1 ? submitAnswers() : next_form()) : null">
                    Submit
                    </button>
                </div>
            </div>
        </div>
    </div>

    <div v-else-if="isLoading" class="text-center py-3">
        <div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>
    </div>
    <div v-else class="text-muted text-center">No form tabs found.</div>

    <!-- File preview modal shell -->
     <teleport to="body">
    <div v-if="showFilePreviewModal"  @click.self="closeFilePreview">
        <div class="custom-modal-content">
            <div class="custom-modal-header">
                <h5>File Preview</h5>
                <button type="button" class="custom-modal-close" @click="closeFilePreview">&times;</button>
            </div>
        </div>
    </div>
    </teleport>

    <!-- Request info modal -->
     <teleport to="body">
        <div class="modal fade" id="request_information" tabindex="-1" aria-labelledby="request_information_label"
            aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
                    <div class="modal-header border-bottom-0 cute-header shadow-lg">
                        <p class="modal-title text-white text-xxs" id="request_information_label">Request Information</p>
                        <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal"
                            aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="answered_file_questions.length > 0" class="mb-3">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <p class="text-xxs fw-bold mb-0">Files in current section:</p>
                            </div>
                            <div class="mt-3">
                                <div v-for="q in answered_file_questions" :key="q.section_question_id"
                                    class="file-list-item">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <div class="d-flex align-items-center">
                                            <i class="bi bi-file-earmark me-2 text-primary"></i>
                                            <div>
                                                <div class="text-xxs">{{ q.question_text }}</div>
                                            </div>
                                        </div>
                                        <input type="checkbox" :value="q.section_question_id"
                                            :data-requested_field_text="q.question_text" class="form-check-input ms-3"
                                            @change="toggle_requested_field($event, q)" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-form-group form-group mb-2 d-flex flex-column">
                            <label for="request_info_note"
                                class="modal-form-label form-label text-start text-xxs">Notes</label>
                            <textarea v-model="request_info_note" id="request_info_note"
                                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                                rows="4"></textarea>
                        </div>
                    </div>
                    <div class="modal-footer border-0 p-2 d-flex justify-content-end">
                        <button type="button" class="btn btn-primary-dark btn-sm text-xxs shadow"
                            @click="request_information">Request</button>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
    <div v-if="preview_modal"  @click.self="preview_modal = false">
        <FilePreviewComponent :presigned_url="selected_url" @close="close_the_file_preview" :key="selected_url" />
    </div>
</template>